import React, { useState } from 'react'
import type { RubroRow } from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  normalizeDocumentationFileIds,
  normalizeDocumentationSlots,
  rubroHasValidSelectedSkill
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  buildDefaultBudgetSheetState,
  resolveBudgetSheetReadonlyReason,
  rubroBudgetSlotHasFileOrPages
} from '../../../../../utils/conversationalMode/budgetSheetStateUtils.ts'
import styles from './BudgetSheetCompactEntry.module.scss'

type Props = {
  tempId: string
  rubro: RubroRow
  draftId: string | undefined
  draftLocked: boolean
  isSaving: boolean
  updateRubro: (tempId: string, updates: Partial<Omit<RubroRow, 'tempId'>>) => void
  onOpenFullscreen: (tempId: string) => void
  embedded?: boolean
  onDeclineAgentPlanilla?: () => void
}

const BudgetSheetCompactEntry: React.FC<Props> = ({
  tempId,
  rubro,
  draftId,
  draftLocked,
  isSaving,
  updateRubro,
  onOpenFullscreen,
  embedded = false,
  onDeclineAgentPlanilla
}) => {
  const [includeSuggestedPlanilla, setIncludeSuggestedPlanilla] = useState(true)

  if (!draftId || draftLocked) return null

  const slotsB = normalizeDocumentationSlots(rubro.documentationSlots ?? {})
  const idsB = normalizeDocumentationFileIds(rubro.documentationFileIds ?? {})
  const budgetBusy = rubroBudgetSlotHasFileOrPages(slotsB, idsB)
  const bs = rubro.budgetSheetState
  const ro = resolveBudgetSheetReadonlyReason(
    {
      tempId,
      detectedLabel: rubro.detectedLabel,
      selectedSkillId: rubro.selectedSkillId
    },
    bs,
    budgetBusy
  )
  const canSkill = rubroHasValidSelectedSkill(rubro)
  const saving = isSaving || !draftId

  const handleOpenFullscreen = () => {
    if (!canSkill || ro === 'no_skill' || saving) return
    if (!bs) {
      if (!includeSuggestedPlanilla) return
      updateRubro(tempId, {
        budgetSheetState: buildDefaultBudgetSheetState({
          tempId,
          detectedLabel: rubro.detectedLabel,
          selectedSkillId: rubro.selectedSkillId
        })
      })
    }
    onOpenFullscreen(tempId)
  }

  const openDisabled =
    saving ||
    !canSkill ||
    ro === 'no_skill' ||
    (!bs && !includeSuggestedPlanilla)

  const wrapClass = embedded ? styles.blockEmbedded : styles.block

  const offerIntro = embedded ? (
    <div className={styles.sheetOfferRow}>
      <span className={styles.agentBadge}>Sugerida</span>
      <p className={styles.sheetOfferText}>
        No encontramos una planilla clara en el PDF: podés abrir un borrador listo para editar o cargar presupuesto
        más abajo.
      </p>
    </div>
  ) : (
    <div className={styles.sheetOfferRow}>
      <span className={styles.agentBadge}>Sugerida</span>
      <p className={styles.sheetOfferText}>
        Sin planilla clara en la documentación: abrí un borrador del asistente o usá archivo o páginas cuando los
        cargues.
      </p>
    </div>
  )

  const renderNoSheetActions = () => (
    <div className={styles.actionsRow}>
      <button
        type="button"
        className={styles.primaryInline}
        disabled={openDisabled}
        onClick={handleOpenFullscreen}
      >
        Revisar y editar planilla
      </button>
      <button
        type="button"
        className={styles.ghostLink}
        disabled={saving}
        onClick={() => setIncludeSuggestedPlanilla(false)}
      >
        No usar esta sugerencia
      </button>
    </div>
  )

  return (
    <div className={wrapClass}>
      {!embedded && !(canSkill && bs) ? (
        <h4 className={styles.title}>Planilla de cotización</h4>
      ) : null}
      {!canSkill ? (
        <p className={styles.hint}>
          Elegí el rubro de la lista para poder usar la planilla de cotización sugerida.
        </p>
      ) : null}
      {budgetBusy && !bs && canSkill && (
        <p className={styles.priorityHint}>
          Si ya vinculaste presupuesto por archivo o páginas del PDF, eso tiene prioridad al publicar. El borrador en
          la app sirve como referencia.
        </p>
      )}
      {canSkill && !bs && includeSuggestedPlanilla ? (
        <>
          {!budgetBusy ? offerIntro : null}
          {renderNoSheetActions()}
        </>
      ) : null}
      {canSkill && !bs && !includeSuggestedPlanilla ? (
        <>
          <p className={styles.hint}>
            {embedded
              ? 'Para este rubro no vamos a guardar la planilla sugerida: usá el origen que indiques más abajo.'
              : 'No vamos a incluir la planilla sugerida en este rubro. Podés cargar presupuesto por archivo o páginas cuando corresponda.'}
          </p>
          <button
            type="button"
            className={styles.ghostLink}
            disabled={saving}
            onClick={() => setIncludeSuggestedPlanilla(true)}
          >
            Volver a mostrar la planilla sugerida
          </button>
        </>
      ) : null}
      {canSkill && bs ? (
        <div className={styles.sheetPanel}>
          <div className={styles.sheetTitleRow}>
            <h4 className={styles.sheetMainTitle}>Planilla de cotización</h4>
            <span className={styles.sheetAiChip}>Generada por el agente</span>
          </div>
          <div className={styles.sheetActionRow}>
            <p className={styles.sheetMeta}>
              {bs.rows.length === 0
                ? 'Sin filas todavía'
                : `${bs.rows.length} fila${bs.rows.length === 1 ? '' : 's'} en el borrador`}
            </p>
            <div className={styles.sheetActionGroup}>
              {onDeclineAgentPlanilla ? (
                <button
                  type="button"
                  className={styles.ghostLink}
                  disabled={saving}
                  onClick={onDeclineAgentPlanilla}
                >
                  No usarla
                </button>
              ) : null}
              <button
                type="button"
                className={styles.primaryInline}
                disabled={saving || ro === 'no_skill'}
                onClick={() => onOpenFullscreen(tempId)}
              >
                Ver y editar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default BudgetSheetCompactEntry
